/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string | number} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    const id = this.#normalizeId(productId);
    if (!id) return;

    try {
      let viewedProducts = this.getProducts();

      viewedProducts = viewedProducts.filter((existingId) => existingId !== id);
      viewedProducts.unshift(id);
      viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch {
      // Private mode / quota — fail silently; search still works without history.
    }
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }
  }

  /**
   * Retrieves the list of recently viewed products from localStorage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      const raw = localStorage.getItem(this.#STORAGE_KEY);
      if (!raw) return [];

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        this.clearProducts();
        return [];
      }

      const normalized = parsed
        .map((value) => this.#normalizeId(value))
        .filter(/** @returns {value is string} */ (value) => Boolean(value));

      // Deduplicate while preserving order.
      const unique = [...new Set(normalized)].slice(0, this.#MAX_PRODUCTS);

      if (unique.length !== parsed.length) {
        try {
          localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(unique));
        } catch {
          // Ignore rewrite failures.
        }
      }

      return unique;
    } catch {
      this.clearProducts();
      return [];
    }
  }

  /**
   * @param {unknown} value
   * @returns {string | null}
   */
  static #normalizeId(value) {
    if (value == null) return null;
    const id = String(value).trim();
    if (!id || id === 'undefined' || id === 'null' || id === 'NaN') return null;
    return id;
  }
}
